import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { GreshamHeader } from "../components/GreshamHeader";
import { GreshamFooter } from "../components/GreshamFooter";
import { TeamMemberModal } from "../components/TeamMemberModal";
import { HighQualityImage } from "../components/HighQualityImage";
import { teamMembers, TeamMember } from "../data/teamMembers";

const About: React.FC = () => {
  const textColor = useColorModeValue("gray.800", "white");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current) {
        const scrolled = window.pageYOffset;
        const rate = scrolled * -0.5;
        parallaxRef.current.style.transform = `translate3d(0, ${rate}px, 0)`;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMemberClick = (member: TeamMember) => {
    setSelectedMember(member);
    onOpen();
  };

  return (
    <Box
      minHeight="100vh"
      bg="white"
      css={{
        overscrollBehavior: "none",
        "-webkit-overflow-scrolling": "touch"
      }}
    >
      <GreshamHeader />

      {/* Hero Section with Background Image */}
      <Box
        as="section"
        w="full"
        position="relative"
        h="100vh"
        minH="600px"
        overflow="hidden"
        clipPath="inset(0)"
      >
        {/* Background Image Container with Parallax */}
        <Box
          ref={parallaxRef}
          position="fixed"
          top={0}
          left={0}
          width="100vw"
          minWidth="100vw"
          height="120vh"
          backgroundImage="url('/background/patrick-langwallner-gbiGvTv9X_E-unsplash.jpg')"
          backgroundSize="cover"
          backgroundPosition="center center"
          backgroundRepeat="no-repeat"
          filter="brightness(0.7)"
          willChange="transform"
          zIndex={-1}
          className="parallax-background background-image"
          css={{
            transform: "translate3d(0, 0, 0)",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden"
          }}
        />
        {/* Text Container - Fixed proportions */}
        <Box
          position="absolute"
          top="20vh"
          left="5vw"
          w="60vw"
          h="50vh"
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          zIndex={100}
          px={{ base: 4, md: 8 }}
        >
          <Box w="100%" h="100%" display="flex" alignItems="center">
            {/* Main Heading */}
            <Heading
              as="h1"
              fontSize={{ base: "clamp(1rem, 3.5vw, 6rem)", xl: "6xl" }}
              fontWeight="bold"
              color="white"
              fontFamily="'Carmen Sans Bold', sans-serif"
              lineHeight="1.15"
              w="100%"
              className="slide-in-left-delay-1"
            >
              At Mercurian Equity, we deliver for investors by
              <br />
              building businesses that power tomorrow's
              <br />
              economy.
            </Heading>
          </Box>
        </Box>

      </Box>

      {/* Welcome Section */}
      <Box
        as="section"
        w="full"
        py={{ base: 16, md: 20 }}
        px={{ base: 8, md: 16, lg: 20 }}
        bg="white"
        mt="-80px"
        position="relative"
        zIndex={20}
        borderRadius="3xl 3xl 0 0"
        boxShadow="0 -20px 40px rgba(0,0,0,0.05)"
      >
        <Box
          px={{ base: 6, md: 12, lg: 16 }}
          w="full"
        >
          {/* Welcome Heading - Space placeholder */}
          <Box mb={12}>
            <Box height={{ base: "3rem", md: "4rem" }} />
          </Box>

          {/* Two Column Content Layout with Image */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 8, lg: 12 }}>
            {/* Left Column - Text Content */}
            <Box display="flex" alignItems="center" h={{ base: "auto", lg: "400px" }}>
              <Box>
                <Heading
                  as="h3"
                  fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                  fontWeight="bold"
                  color={textColor}
                  fontFamily="'Carmen Sans Bold', sans-serif"
                  lineHeight="1.2"
                  letterSpacing="0.01em"
                  mb={8}
                >
                  Solutions to help achieve your investing goals.
                </Heading>

                <VStack spacing={6} align="flex-start">
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  lineHeight="1.6"
                  fontWeight="300"
                  opacity={0.8}
                >
                  Mercurian Equity is an investment management firm, focused on delivering investment solutions and strategic capital partnerships for global institutions and wholesale clients.
                </Text>

                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  lineHeight="1.6"
                  fontWeight="300"
                  opacity={0.8}
                >
                  Our model is based on trust and investing with purpose. Our focus is on small and medium enterprises that make up the lower and middle market of our economy. Our ethos is to help build businesses that are leaders in their sector and are making positive contributions to the broader economy.
                </Text>
                </VStack>
              </Box>
            </Box>

            {/* Right Column - Dynamic Image */}
            <Box>
              <Box
                w="full"
                h={{ base: "300px", md: "400px" }}
                borderRadius="lg"
                position="relative"
                overflow="hidden"
                backgroundImage="url('/background/katie-moum-9jNcTRncjgM-unsplash.jpg')"
                backgroundSize="cover"
                backgroundPosition="center"
                backgroundRepeat="no-repeat"
                css={{
                  position: "relative",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "rgba(212, 175, 55, 0.1)",
                    zIndex: 1
                  }
                }}
              >
                {/* Subtle overlay to enhance readability */}
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  w="full"
                  h="full"
                  zIndex={2}
                  css={{
                    background: `
                      linear-gradient(135deg,
                        rgba(212, 175, 55, 0.15) 0%,
                        rgba(244, 208, 63, 0.1) 50%,
                        rgba(230, 199, 103, 0.05) 100%
                      )
                    `
                  }}
                />
              </Box>
            </Box>
          </SimpleGrid>
        </Box>
      </Box>

      {/* CEO Section */}
      <Box
        as="section"
        w="full"
        py={{ base: 16, md: 20 }}
        px={{ base: 8, md: 16, lg: 20 }}
        mb={{ base: 8, md: 12 }}
      >
        <Box
          py={{ base: 12, md: 16 }}
          px={{ base: 6, md: 12, lg: 16 }}
        >
          {/* CEO Heading */}
          <Box mb={12}>
            <Box
              position="relative"
              display="inline-block"
              cursor="pointer"
              _hover={{
                "& .underline-animation": {
                  transform: "scaleX(1)"
                }
              }}
            >
              <Heading
                as="h3"
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                fontWeight="bold"
                color={textColor}
                fontFamily="'Carmen Sans Bold', sans-serif"
                letterSpacing="0.01em"
                textTransform="uppercase"
                textAlign="left"
              >
                OUR CEO
              </Heading>
              <Box
                className="underline-animation"
                position="absolute"
                bottom="-8px"
                left={0}
                w="100%"
                h="1px"
                bg={textColor}
                transform="scaleX(0)"
                transformOrigin="left"
                transition="transform 0.3s ease"
              />
            </Box>
          </Box>

          {/* CEO Content Layout */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 8, lg: 12 }}>
            {/* Left Column - CEO Photo */}
            <Box display="flex" justifyContent="center" alignItems="center">
              <Box
                w="full"
                maxW="500px"
                aspectRatio="3/4"
                borderRadius="lg"
                position="relative"
                overflow="hidden"
                mx="auto"
                bg="transparent"
              >
                <HighQualityImage
                  src="/photo/Stephen-web.jpg"
                  alt="Stephen Chipfunde - CEO of Mercurian Equity"
                  width="100%"
                  height="100%"
                  objectFit="cover"
                  objectPosition="center top"
                />
              </Box>
            </Box>

            {/* Right Column - CEO Content */}
            <Box display="flex" alignItems="center" h={{ base: "auto", lg: "500px" }}>
              <Box>
                <Heading
                  as="h4"
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  fontWeight="500"
                  color={textColor}
                  fontFamily="'Carmen Sans Medium', sans-serif"
                  mb={6}
                >
                  Stephen Chipfunde
                </Heading>


                <VStack spacing={6} align="flex-start">
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color={textColor}
                    fontFamily="'Carmen Sans Light', sans-serif"
                    lineHeight="1.6"
                    fontWeight="normal"
                    opacity={0.8}
                  >
                    Stephen is the chief executive officer of Mercurian Equity Pty Ltd. Stephen is an experienced corporate strategist and operations improvement specialist.
                  </Text>

                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color={textColor}
                    fontFamily="'Carmen Sans Light', sans-serif"
                    lineHeight="1.6"
                    fontWeight="normal"
                    opacity={0.8}
                  >
                    In this role, he sets and drives the strategic goals of the company, investment framework and oversees day to day management of the firm.
                  </Text>

                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color={textColor}
                    fontFamily="'Carmen Sans Light', sans-serif"
                    lineHeight="1.6"
                    fontWeight="normal"
                    opacity={0.8}
                  >
                    He has experience in private markets, with a primary focus on driving operational efficiency across a variety of businesses and industries in Australia.
                  </Text>

                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color={textColor}
                    fontFamily="'Carmen Sans Light', sans-serif"
                    lineHeight="1.6"
                    fontWeight="normal"
                    opacity={0.8}
                  >
                    Stephen holds a postgrad in Corporate Finance from Wharton, an MBA (Strategy & Finance) from Curtin Graduate School of Business and a Bachelor of Engineering (Chemical) from Curtin University. He is also a Graduate from the Australian Institute of Company Directors.
                  </Text>
                </VStack>
              </Box>
            </Box>
          </SimpleGrid>
        </Box>
      </Box>

      {/* Team Section */}
      <Box
        as="section"
        w="full"
        py={{ base: 16, md: 20 }}
        px={{ base: 8, md: 16, lg: 20 }}
        mb={{ base: 8, md: 12 }}
      >
        <Box
          py={{ base: 12, md: 16 }}
          px={{ base: 6, md: 12, lg: 16 }}
        >
          {/* Advisory Board Heading */}
          <Box mb={12}>
            <Box
              position="relative"
              display="inline-block"
              cursor="pointer"
              _hover={{
                "& .underline-animation": {
                  transform: "scaleX(1)"
                }
              }}
            >
              <Heading
                as="h3"
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                fontWeight="bold"
                color={textColor}
                fontFamily="'Carmen Sans Bold', sans-serif"
                letterSpacing="0.01em"
                textTransform="uppercase"
                textAlign="left"
              >
                ADVISORY BOARD
              </Heading>
              <Box
                className="underline-animation"
                position="absolute"
                bottom="-8px"
                left={0}
                w="100%"
                h="1px"
                bg={textColor}
                transform="scaleX(0)"
                transformOrigin="left"
                transition="transform 0.3s ease"
              />
            </Box>
          </Box>

          {/* Advisory Board Members Grid */}
          <Box w="full">
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={{ base: 8, lg: 8 }}>
              {teamMembers.filter(member => member.id !== "stephen-chipfunde").map((member) => (
                <Box key={member.id} textAlign="center">
                  <Box display="flex" justifyContent="center" alignItems="center" mb={4}>
                    <Box
                      w="full"
                      maxW="380px"
                      aspectRatio="3/4"
                      bg="transparent"
                      borderRadius="lg"
                      position="relative"
                      overflow="hidden"
                      cursor="pointer"
                      transition="all 0.3s ease"
                      mx="auto"
                      _hover={{
                        transform: "translateY(-4px)"
                      }}
                      onClick={() => handleMemberClick(member)}
                    >
                      {member.image && (
                        <Box
                          w="100%"
                          h="100%"
                          position="relative"
                          overflow="hidden"
                        >
                          <Box
                            w="100%"
                            h="100%"
                            transform={member.imageStyle?.scale ? `scale(${member.imageStyle.scale}) translateY(${member.imageStyle.objectPosition?.includes('90%') ? '7%' : member.imageStyle.objectPosition?.includes('80%') ? '3%' : '0'})` : undefined}
                            transformOrigin="center center"
                          >
                            <HighQualityImage
                              src={member.image}
                              alt={`${member.name} - ${member.title}`}
                              width="100%"
                              height="100%"
                              objectFit="cover"
                              objectPosition="center center"
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Heading
                    as="h4"
                    fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
                    fontWeight="500"
                    color={textColor}
                    fontFamily="'Carmen Sans Medium', sans-serif"
                    mb={2}
                  >
                    {member.name}
                  </Heading>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="#9f8f76"
                    fontFamily="'Carmen Sans Light', sans-serif"
                    fontWeight="300"
                    lineHeight="1.4"
                  >
                    {member.title}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        </Box>
      </Box>

      <GreshamFooter />

      {/* Team Member Modal */}
      <TeamMemberModal
        isOpen={isOpen}
        onClose={onClose}
        member={selectedMember}
      />
    </Box>
  );
};

export default About;